import React from 'react';
import styles from './ProjectsTable.module.scss';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon } from '../../../assets/icons/icons';
import { Checkbox } from '../../ui/Checkbox/Checkbox';
import type { Project, SortKey, SortState } from '../../../types/projectsTable';
import { ProjectRow } from './ProjectRow';
import { DataTable, tableStyles } from '../../ui/DataTable/DataTable';
import { SortableTh } from '../../ui/DataTable/SortableTh';

interface Column {
    key: SortKey | null;
    title: string;
    width?: string;
    align?: 'right';
}

const COLUMNS: Column[] = [
    { key: null, title: '', width: '38px' },
    { key: 'name', title: 'Проект' },
    { key: 'author', title: 'Заказчик', width: '160px' },
    { key: 'date', title: 'Создан', width: '134px' },
    { key: 'price', title: 'Цена, ₽', width: '134px', align: 'right' },
    { key: 'status', title: 'Статус', width: '118px' },
    { key: 'pos', title: 'Позиции', width: '112px', align: 'right' },
    { key: null, title: '', width: '48px' },
];

interface ProjectsTableProps {
    rows: Project[];
    selected: Set<string>;
    sort: SortState;
    scrollKey?: string | number;
    onToggleRow: (id: string) => void;
    onTogglePage: () => void;
    onSort: (key: SortKey) => void;
    onDelete: (project: Project) => void;
    bulkBar?: ReactNode;
    footer?: ReactNode;
}

export function ProjectsTable({
    rows,
    selected,
    sort,
    scrollKey,
    onToggleRow,
    onTogglePage,
    onSort,
    onDelete,
    bulkBar,
    footer,
}: ProjectsTableProps) {
    const selectedOnPage = rows.filter((project) => selected.has(project.id)).length;
    const allSelected = rows.length > 0 && selectedOnPage === rows.length;
    const someSelected = selectedOnPage > 0 && !allSelected;

    const navigate = useNavigate();

    return (
        <DataTable
            toolbar={bulkBar}
            footer={footer}
            scrollKey={scrollKey}
            colgroup={
                <colgroup>
                    {COLUMNS.map((column, index) => (
                        <col key={index} style={column.width ? { width: column.width } : undefined} />
                    ))}
                </colgroup>
            }
            empty={
                rows.length === 0 ? (
                    <div className={tableStyles.emptyState}>
                        <SearchIcon />
                        <b>Ничего не нашлось</b>
                        <p>Измените запрос или снимите фильтры.</p>
                    </div>
                ) : undefined
            }
        >
            <thead>
                <tr>
                    <th>
                        <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            label="Выбрать все проекты на странице"
                            onChange={onTogglePage}
                        />
                    </th>

                    {COLUMNS.slice(1, -1).map((column) => (
                        <SortableTh
                            key={column.key}
                            title={column.title}
                            active={sort.key === column.key}
                            dir={sort.dir}
                            align={column.align}
                            onClick={() => column.key && onSort(column.key)}
                        />
                    ))}

                    <th />
                </tr>
            </thead>

            <tbody>
                {rows.map((project) => (
                    <ProjectRow
                        key={project.id}
                        project={project}
                        selected={selected.has(project.id)}
                        onToggle={onToggleRow}
                        onOpen={(item) => navigate(`/projects/${item.id}`)}
                        onDelete={onDelete}
                    />
                ))}
            </tbody>
        </DataTable>
    );
}
